import { NgxLoggerLevel } from 'ngx-logger';

export const REDACTED = '[REDACTED]';

const SENSITIVE_KEY = /(?:authorization|proxyauthorization|cookie|setcookie|apikey|secret|token|password|credential|signature|signedurl|paymenturl|downloadurl|clientsecret|idtoken|accesstoken|refreshtoken|privatekey)/i;
const CUSTOMER_PAYLOAD_KEY = /(?:body|payload|currentparamvalues|formvalue|schema|spec|record|userdata|projectdata|organizationdata|invitationdata|billingdata)/i;
const PERSONAL_DATA_KEY = /^(?:email|fullname|displayname|phone|address|picture|avatarurl)$/i;

function normalizeKey(key: string): string {
  return key.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function redactString(value: string): string {
  return value
    .replace(/\bBearer\s+[^\s,;]+/gi, `Bearer ${REDACTED}`)
    .replace(
      /([?&](?:token|key|signature|x-goog-signature|x-amz-signature|x-goog-credential|x-amz-credential)=)[^&#\s]*/gi,
      `$1${REDACTED}`,
    )
    .replace(
      /\b(authorization|proxy-authorization|cookie|set-cookie|api[-_]?key|access[-_]?token|refresh[-_]?token|id[-_]?token|client[-_]?secret)\s*[:=]\s*[^,;\s]+/gi,
      `$1=${REDACTED}`,
    );
}

export function redactSensitiveData(
  value: unknown,
  seen: WeakSet<object> = new WeakSet<object>(),
  depth = 0,
): unknown {
  if (typeof value === 'string') {
    return redactString(value);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (depth >= 8) {
    return '[TRUNCATED]';
  }
  if (seen.has(value)) {
    return '[CIRCULAR]';
  }
  seen.add(value);

  if (value instanceof Error) {
    return {
      name: value.name,
      message: redactString(value.message),
      stack: value.stack ? redactString(value.stack) : undefined,
    };
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveData(item, seen, depth + 1));
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => {
      const normalizedKey = normalizeKey(key);
      if (
        SENSITIVE_KEY.test(normalizedKey) ||
        CUSTOMER_PAYLOAD_KEY.test(normalizedKey) ||
        PERSONAL_DATA_KEY.test(normalizedKey)
      ) {
        return [key, REDACTED];
      }
      return [key, redactSensitiveData(nestedValue, seen, depth + 1)];
    }),
  );
}

export function shouldRetainLog(
  messageLevel: NgxLoggerLevel,
  configuredLevel: NgxLoggerLevel,
): boolean {
  return configuredLevel !== NgxLoggerLevel.OFF && messageLevel >= configuredLevel;
}
